import React from "react";
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import { Star, Shield, Zap, ChevronRight, Crown, Sparkles, Award, CheckCircle2, Droplets } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const FeaturedVendorsSection = ({ vendors, isLoaded }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // ── P0 Build 20 fix (2026-06-18, Phase 7g.15.110) ────────────────
  // Build 19 produced a full black screen on Android because the
  // upstream `vendors` prop arrived as a non-array (object wrapper or
  // HTML-error-page parsed by axios) and `vendors.map` threw at
  // mount, blowing up the React root.  Treat any non-array as empty.
  const safeVendors = Array.isArray(vendors) ? vendors : [];
  if (safeVendors.length === 0) return null;

  return (
    <section className="relative py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-12 transition-all duration-1000 delay-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/30 mb-4">
            <Award className="w-4 h-4 text-amber-400" />
            <span className="text-amber-400 text-sm font-semibold" data-testid="featured-vendors-approval-label">{t('review368.approvedVendors')}</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" data-testid="featured-vendors-title">{t('review368.featuredTitle')}</h2>
          <p className="text-slate-400 max-w-xl mx-auto" data-testid="featured-vendors-description">{t('review368.featuredDescription')}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {safeVendors.map((vendor, idx) => (
            <Card
              key={vendor.vendor_id || `vendor-${idx}`}
              data-testid={`featured-vendor-${idx}`}
              className="group bg-slate-900/60 backdrop-blur-md border border-white/10 hover:border-cyan-500/30 rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-xl hover:shadow-cyan-500/10 hover:-translate-y-1 cursor-pointer"
              onClick={() => navigate(`/vendors/${vendor.vendor_id}`)}
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <Avatar className="w-14 h-14 border-2 border-cyan-500/30">
                    <AvatarImage src={vendor.logo} />
                    <AvatarFallback className="bg-gradient-to-br from-cyan-500 to-blue-600 text-white text-lg font-bold">
                      {vendor.business_name?.charAt(0) || 'W'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-white font-semibold truncate">{vendor.business_name}</h3>
                      {vendor.is_verified && <CheckCircle2 className="w-4 h-4 text-cyan-400 flex-shrink-0" />}
                    </div>
                    <div className="flex items-center gap-1 mt-1">
                      <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                      <span className="text-white text-sm font-medium" data-testid={`featured-rating-${idx}`}>{vendor.total_reviews > 0 && Number.isFinite(vendor.rating) ? vendor.rating : t('review368.noRating')}</span>
                      <span className="text-slate-500 text-sm">({vendor.total_reviews || 0})</span>
                    </div>
                  </div>
                </div>
                
                {(vendor.listing_tier === 'elite' || vendor.is_fleet_certified) && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {vendor.listing_tier === 'elite' && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500/20 text-amber-400 text-xs font-medium">
                        <Crown className="w-3 h-3" /> {t('review368.elite')}
                      </span>
                    )}
                    {vendor.is_fleet_certified && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-cyan-500/20 text-cyan-400 text-xs font-medium">
                        <Shield className="w-3 h-3" /> {t('review368.fleetCertified')}
                      </span>
                    )}
                  </div>
                )}

                <Button
                  className="w-full mt-4 bg-white/5 hover:bg-white/10 text-white font-medium rounded-xl border border-white/10 group-hover:border-cyan-500/30 transition-all"
                  onClick={(e) => { e.stopPropagation(); navigate(vendor.bookable ? `/book?vendor=${vendor.vendor_id}` : `/vendors/${vendor.vendor_id}`); }}
                  data-testid={`book-vendor-${idx}`}
                >
                  {t(vendor.bookable ? 'review368.bookNow' : 'review368.viewDetails')}
                  <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center mt-10">
          <Button
            variant="outline"
            onClick={() => navigate("/vendors")}
            className="border-white/10 text-slate-300 hover:text-white hover:bg-white/5 rounded-full px-8"
            data-testid="view-all-vendors"
          >
            {t('review368.viewAll')}
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default FeaturedVendorsSection;
